import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, WarningCircle, Clock, XCircle, X, CaretLeft, CaretRight, Table } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { wibNow, wibDateStr } from "@/lib/time";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const MON = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const WEEKDAYS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat"];

function mondayOf(d) {
  const x = new Date(d);
  const day = x.getDay();
  x.setDate(x.getDate() + (day === 0 ? -6 : 1 - day));
  x.setHours(0, 0, 0, 0);
  return x;
}
const dstr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function cellStyle(rec, dateStr, todayStr) {
  if (rec && rec.status === "Izin") return { cls: "bg-stone-200 text-stone-500", txt: "Izin" };
  if (rec && rec.time_in) {
    const [h, m] = rec.time_in.split(".").map(Number);
    const mins = h * 60 + m;
    const t = rec.time_in.replace(".", ":");
    if (mins <= 7 * 60 + 10) return { cls: "bg-green-100 text-green-700", txt: t };
    if (mins <= 7 * 60 + 30) return { cls: "bg-yellow-100 text-yellow-700", txt: t };
    return { cls: "bg-red-100 text-red-600", txt: t };
  }
  if (dateStr > todayStr) return { cls: "bg-white text-stone-300", txt: "" };
  return { cls: "bg-stone-100 text-stone-400", txt: "-" };
}

export default function Riwayat() {
  const now = wibNow();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const [izinDate, setIzinDate] = useState(wibDateStr());
  const [reason, setReason] = useState("");

  const load = () => api.get(`/teacher/attendance/me?month=${month}&year=${year}`).then((r) => setRecords(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [month, year]); // eslint-disable-line

  const [showDetail, setShowDetail] = useState(false);
  const [allRecs, setAllRecs] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("izin") === "1") setOpen(true);
    if (searchParams.get("detail") === "1") setShowDetail(true);
  }, [searchParams]);
  useEffect(() => { api.get("/teacher/attendance/me").then((r) => setAllRecs(r.data)).catch(() => {}); }, []);
  const recsMap = {};
  allRecs.forEach((r) => { recsMap[r.date] = r; });
  const weekStart = mondayOf(wibNow());
  weekStart.setDate(weekStart.getDate() + weekOffset * 7);
  const weekDates = [0, 1, 2, 3, 4].map((i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const todayStr = dstr(wibNow());
  const rangeLabel = `${weekDates[0].getDate()} ${MON[weekDates[0].getMonth()]} – ${weekDates[4].getDate()} ${MON[weekDates[4].getMonth()]} ${weekDates[4].getFullYear()}`;

  const [note, setNote] = useState(null);
  const [reply, setReply] = useState("");
  const loadNote = () => api.get("/teacher/notes/me").then((r) => { setNote(r.data); }).catch(() => {});
  useEffect(() => { loadNote(); }, []); // eslint-disable-line
  const submitReply = async () => {
    if (!reply.trim()) { toast.error("Isi jawaban"); return; }
    try { await api.put("/teacher/notes/me/reply", { reply }); toast.success("Jawaban terkirim"); setReply(""); loadNote(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const submitIzin = async () => {
    if (!reason.trim()) { toast.error("Isi alasan izin"); return; }
    try { await api.post("/teacher/attendance/izin", { date: izinDate, reason }); toast.success("Izin tercatat"); setOpen(false); setReason(""); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const meta = (s) => {
    if (s === "Hadir") return { c: "bg-green-100 text-green-700", I: CheckCircle };
    if (s === "Terlambat") return { c: "bg-amber-100 text-amber-700", I: Clock };
    if (s === "Izin") return { c: "bg-sky-100 text-sky-700", I: WarningCircle };
    return { c: "bg-red-100 text-red-600", I: XCircle };
  };

  return (
    <div>
      <PageHeader title="Riwayat Kehadiran" back={false} />
      <div className="px-5 pt-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
          {MON.map((m, i) => (
            <button key={m} data-testid={`riwayat-bulan-${i + 1}`} onClick={() => setMonth(i + 1)}
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold ${month === i + 1 ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>{m}</button>
          ))}
        </div>

        <button data-testid="riwayat-izin-btn" onClick={() => setOpen(true)}
          className="w-full mb-4 py-3 rounded-2xl border-2 border-amber-400 text-amber-600 font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <WarningCircle size={20} weight="fill" /> Ajukan Izin
        </button>

        <button data-testid="riwayat-detail-btn" onClick={() => setShowDetail((v) => !v)}
          className="w-full mb-4 py-3 rounded-2xl border-2 border-primary text-primary font-semibold flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <Table size={20} weight="bold" /> {showDetail ? "Sembunyikan Detail" : "Detail Kehadiran (Tabel Mingguan)"}
        </button>

        {showDetail && (
          <div className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4 mb-4" data-testid="riwayat-weekly-table-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-stone-800 text-sm">Jam Masuk per Hari</h3>
              <div className="flex items-center gap-1.5">
                <button data-testid="riwayat-week-prev" onClick={() => setWeekOffset((w) => w - 1)} className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center active:scale-90 transition-transform"><CaretLeft size={16} weight="bold" /></button>
                <button data-testid="riwayat-week-next" onClick={() => setWeekOffset((w) => Math.min(0, w + 1))} disabled={weekOffset >= 0}
                  className={`w-8 h-8 rounded-full flex items-center justify-center active:scale-90 transition-transform ${weekOffset >= 0 ? "bg-stone-50 text-stone-300" : "bg-stone-100"}`}><CaretRight size={16} weight="bold" /></button>
              </div>
            </div>
            <p className="text-xs text-stone-500 mb-3" data-testid="riwayat-week-range">{rangeLabel}{weekOffset === 0 ? " (minggu ini)" : ""}</p>
            <div className="overflow-x-auto no-scrollbar -mx-1">
              <table className="w-full border-collapse text-xs rounded-xl overflow-hidden" data-testid="riwayat-weekly-table">
                <thead>
                  <tr className="bg-teal-800 text-white">
                    {weekDates.map((d, i) => (
                      <th key={i} className="font-bold px-2 py-2 text-center min-w-[64px]">{WEEKDAYS[i]}<br /><span className="text-teal-100 font-semibold">{d.getDate()}/{d.getMonth() + 1}</span></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-teal-50/60">
                    {weekDates.map((d, i) => {
                      const ds = dstr(d);
                      const st = cellStyle(recsMap[ds], ds, todayStr);
                      return <td key={i} className="px-1 py-1.5 text-center border-t border-teal-100"><span className={`inline-block w-full rounded-lg py-1.5 font-bold ${st.cls}`}>{st.txt}</span></td>;
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3 text-[11px]">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 inline-block" /> Tepat waktu (≤07:10)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-200 inline-block" /> 07:11–07:30</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-200 inline-block" /> Terlambat (&gt;07:30)</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-stone-200 inline-block" /> Izin / Tidak hadir</span>
            </div>
          </div>
        )}

        {note?.note && (
          <div data-testid="admin-note-card" className="mb-4 bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-1">📌 Catatan dari Admin</p>
            <p className="text-sm text-stone-700 mb-3">{note.note}</p>
            {note.reply && (
              <div className="bg-white rounded-xl p-2.5 mb-2 border border-amber-100">
                <p className="text-[11px] text-stone-400 mb-0.5">Jawaban Anda:</p>
                <p className="text-sm text-stone-700">{note.reply}</p>
              </div>
            )}
            <textarea data-testid="admin-note-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
              placeholder="Tulis jawaban untuk admin..." className="w-full bg-white border border-amber-200 rounded-xl py-2 px-3 text-sm outline-none focus:border-amber-400 resize-none mb-2" />
            <button data-testid="admin-note-reply-btn" onClick={submitReply}
              className="w-full bg-amber-500 text-white font-semibold py-2.5 rounded-full text-sm active:scale-95 transition-transform">Kirim Jawaban</button>
          </div>
        )}

        <div className="space-y-2">
          {records.map((h, i) => {
            const { c, I } = meta(h.status);
            return (
              <div key={i} data-testid={`riwayat-item-${i}`} className="bg-white rounded-2xl border border-stone-200/60 p-3.5 flex items-center justify-between shadow-sm">
                <div>
                  <p className="text-sm font-semibold text-stone-700">{new Date(h.date).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "short" })}</p>
                  {h.time_in && <p className="text-xs text-stone-400">Masuk {h.time_in} {h.time_out ? `• Pulang ${h.time_out}` : ""}</p>}
                  {h.reason && <p className="text-xs text-stone-400">{h.reason}</p>}
                </div>
                <span className={`text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 ${c}`}><I size={13} weight="fill" /> {h.status}</span>
              </div>
            );
          })}
          {records.length === 0 && <p className="text-center text-stone-400 mt-8">Belum ada rekaman bulan ini.</p>}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setOpen(false)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Ajukan Izin</h3>
              <button onClick={() => setOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-xs text-stone-400 mb-1">Tanggal Izin</p>
            <input data-testid="izin-date" type="date" value={izinDate} onChange={(e) => setIzinDate(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-3 outline-none focus:border-primary" />
            <p className="text-xs text-stone-400 mb-1">Alasan Izin</p>
            <textarea data-testid="izin-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Tuliskan alasan izin..." className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-4 outline-none focus:border-primary resize-none" />
            <button data-testid="izin-submit" onClick={submitIzin} className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform">Submit Izin</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}
